import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Customers › Waitlist (`cust-wait`), from `GET /v1/admin/waitlist`.
 *
 * # This is a count of refusals, not a measure of demand
 *
 * A row exists because somebody completed the sign-up form and
 * `POST /v1/registrations` refused them for a closed country. Everyone who
 * never reached the form is absent, so the figures are **not comparable between
 * countries with different marketing reach** — and a queue of email addresses
 * looks exactly like a complete list of interested people, which is why the
 * backend sends the caveat rather than leaving a console to think of it.
 *
 * # The corpus screen this replaces drew the one column the backend asked
 * # nobody to draw
 *
 * `cust-wait` had four invented people and a **"Platform declared"** column
 * carrying Fansly, Patreon and Ko-fi. The response says:
 *
 * > *"`declared_platform` IS EMPTY ON EVERY ROW AND THAT IS STRUCTURAL, NOT A
 * > CONTENT GAP. The registration request carries country, legal name, email,
 * > kind and the referral pair — it has no field for which platform pays the
 * > person, so the only writer of this table has nothing to record. […] Do not
 * > render the design's 'Platform declared' column from a fixture."*
 *
 * So there is **no such column here at all** — not an empty one. A blank cell
 * reads as "they declined to say", which is a claim about the person rather
 * than about the form they filled in.
 *
 * # `onboarding_open` is TRI-STATE and the third state is the common one
 *
 * Absent means the country has no `jurisdictions` row: nobody has assessed it.
 * That is a different fact from a country assessed and closed, and the two lead
 * to different desks — the first needs a mandate decision, the second a
 * passport notification. `country_assessed` carries the distinction, *"because
 * a missing JSON key and `false` are the same thing to a careless client"*.
 *
 * # There is no invite control, and it is not simply unbuilt
 *
 * > *"NOBODY HERE CONSENTED TO BEING CONTACTED… These are email addresses given
 * > so an account could be opened, on registrations we refused."*
 *
 * Sending to them needs a lawful basis this table does not record. The read
 * seam has no write method of any kind, so there is nothing to enable — and a
 * greyed Invite button naming a role would send an operator to ask for a
 * permission that does not exist, of somebody who could not grant it. Law 18's
 * exception, the same one `cf-reasons` takes.
 */

export const WAITLIST_PATH = '/waitlist'

const SOURCE = 'postgres · registration_waitlist (GET /v1/admin/waitlist)'
const AUTHORITY = 'operational'

interface Entry {
  readonly id: number | null
  readonly email: string
  readonly countryCode: string
  readonly subjectKind: string
  readonly state: string
  readonly capturedAt: string
  readonly invitedAt: string
  readonly invitedBy: string
  /**
   * SERVED, and tri-state. `null` is "no jurisdiction row exists", which is not
   * `false`. Read together with `countryAssessed`, which is the field that says
   * whether the absence means anything.
   */
  readonly onboardingOpen: boolean | null
  readonly countryAssessed: boolean
}

function readEntry(raw: unknown): Entry | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const email = str(o['email'])
  if (email === '') return null
  return {
    id: int(o['id']),
    email,
    countryCode: str(o['country_code']),
    subjectKind: str(o['subject_kind']),
    state: str(o['state']),
    capturedAt: str(o['captured_at']),
    invitedAt: str(o['invited_at']),
    invitedBy: str(o['invited_by']),
    onboardingOpen: typeof o['onboarding_open'] === 'boolean' ? o['onboarding_open'] : null,
    countryAssessed: o['country_assessed'] === true,
  }
}

/**
 * The columns, and the one that is deliberately not among them.
 *
 * No "Platform declared". See the module header — the backend's own response
 * asks for its absence by name.
 */
const COLS: readonly Column[] = [
  ['Email', 'x', 'minmax(220px,1.6fr)'],
  ['Country', 'm', '92px'],
  ['Their country', 'c', 'minmax(190px,1.3fr)'],
  ['Kind', 'm', '110px'],
  ['Refused', 'm', '120px'],
  ['State', 'c', '124px'],
  ['Invited by', 'x', 'minmax(140px,1fr)'],
]

/**
 * The country cell, and the three answers it has to keep apart.
 *
 * The middle one is the one a console gets wrong. An unassessed country is not
 * a closed country: nobody has looked at it, and the next step is a mandate
 * decision rather than a passport notification. Rendering the missing key as
 * `false` would put those people in the queue of "assessed and refused", where
 * the action available is one that does not apply to them.
 */
function countryCell(e: Entry): ChipCell {
  if (e.onboardingOpen === null) {
    return e.countryAssessed
      // Assessed but no answer served is a shape the backend does not produce —
      // `country_assessed` is computed FROM the pointer being non-nil — so
      // seeing it means the two fields disagree and neither can be trusted.
      ? ['— read disagrees with itself', 'r']
      : ['never assessed', 'a']
  }
  return e.onboardingOpen
    ? ['open now — they could be invited', 'g']
    : ['assessed and closed', 'n']
}

function stateCell(e: Entry): ChipCell {
  switch (e.state) {
    case '': return ['— not stated', 'r']
    case 'held': return ['held', 'a']
    // Reachable only with a named operator attached (migration 152), and
    // nothing in this build reaches it. A row reading `invited` is one somebody
    // moved outside this console.
    case 'invited': return ['invited', 'g']
    default: return [e.state, 'n']
  }
}

function row(e: Entry): readonly Cell[] {
  return [
    e.email,
    e.countryCode === '' ? '—' : e.countryCode,
    countryCell(e),
    e.subjectKind === '' ? '— not stated' : e.subjectKind,
    e.capturedAt === '' ? '—' : day(e.capturedAt),
    stateCell(e),
    invitedBy(e),
  ]
}

/**
 * Who invited them, and when nobody did.
 *
 * An `invited` row with no operator on it is worth reporting rather than
 * shrugging at: the transition exists in that shape precisely so an invitation
 * cannot arrive as a silent edit of the row that records the refusal.
 */
function invitedBy(e: Entry): Cell {
  if (e.state !== 'invited') return '— nobody is invited from here'
  if (e.invitedBy === '') return '— UNATTRIBUTED'
  return e.invitedAt === '' ? e.invitedBy : `${e.invitedBy} · ${day(e.invitedAt)}`
}

/** The five notes the response carries, rendered as the backend wrote them. */
const REQUIRED_NOTES: readonly (readonly [key: string, kicker: string, tone: 'a' | 'n' | 'r'])[] = [
  ['capture_note', 'Not a measure of demand', 'r'],
  ['platform_note', 'The column that is missing on purpose', 'a'],
  ['invite_note', 'Nobody here consented to being contacted', 'r'],
  ['country_note', 'What an open country means here', 'n'],
  ['limit_note', 'The list is capped', 'a'],
]

export function composeWaitlist(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['rows']) ? (payload['rows'] as readonly unknown[]) : []
  const rows: Entry[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readEntry(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const openable = rows.filter((e) => e.onboardingOpen === true).length
  const unassessed = rows.filter((e) => e.onboardingOpen === null && !e.countryAssessed).length

  const table: TableBlock = {
    t: 'table',
    title: 'People a closed country refused',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} row(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Longest-waiting first, and that ordering is the backend’s. Every row is somebody who filled in the sign-up form and was turned away because their country is closed — not somebody who asked to be told when it opens.',
  }

  const blocks: Block[] = [table]

  if (openable > 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Something changed',
      title: `${String(openable)} of these people are in a country that is open now`,
      body: 'Their country has opened since they were refused, so the reason they are on this list no longer applies. What follows from that is a conversation about lawful basis rather than a control on this screen — nobody here agreed to be contacted, and this console has no way to send anything.',
      ref: 'onboarding_open is true on those rows',
    })
  }

  if (unassessed > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Never assessed',
      title: `${String(unassessed)} row(s) name a country nobody has assessed at all`,
      body: 'There is no jurisdiction row for those countries, which is not the same as a country that was looked at and closed. The next step for an unassessed country is a mandate decision; for an assessed and closed one it is a passport notification. This screen keeps them apart because the read does — a console that took the missing field for a `false` would put both in the same queue.',
      ref: 'country_assessed is false on those rows',
    })
  }

  for (const [key, kicker, tone] of REQUIRED_NOTES) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} waitlist row(s) could not be read`,
      body: 'Counted rather than dropped silently. A row with no email address is not a person this list can be about, and a waitlist quietly one row shorter than the record understates a refusal that happened.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Empty',
      title: 'Nobody has been refused for a closed country',
      body: 'That is the correct state of a deployment where every attempted sign-up came from an open country, and it is equally the correct state of one nobody has tried to sign up to. It is NOT a statement that there is no demand from closed countries: somebody who never reached the sign-up form never became a row here.',
      ref: 'an empty waitlist is not an absence of demand',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  // Always. The design's column has no writer, so nothing in this deployment
  // can ever fill it, and saying that is more use than an empty column.
  missing.push('which platform pays each person — the sign-up form has no such field')
  missing.push('anyone who never reached the sign-up form')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  // Counted from what was read, never from the corpus. The inspector used to
  // draw `customers · 1,318 rows` beside a table holding one.
  const subject = `registration_waitlist · ${String(rows.length)} row(s) read`
  return { blocks, provenance, subject }
}

export const loadWaitlist: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(WAITLIST_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The waitlist endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The waitlist endpoint returned a shape this console does not know.', null)
  }
  return composeWaitlist(payload as Record<string, unknown>, new Date())
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The waitlist could not be read',
      body: `${reason} Nothing is drawn. In particular this is NOT an empty waitlist: whether anybody was refused for a closed country is unknown from here, and "nobody was turned away" is a different answer from "we could not find out".`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
